// frontend/src/components/engineer/pages/CreateOvertimePage.tsx - COMPLETE ANT DESIGN REWRITE
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  Switch,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
  Divider,
  Steps,
  Row,
  Col,
  Statistic,
  List,
  Avatar,
  Result,
  message,
  Collapse,
  Upload,
  Empty,
  Checkbox,
  Badge,
  Descriptions
} from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  FileExcelOutlined,
  WarningOutlined,
  ProjectOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  MoonFilled,
  ReloadOutlined,
  CheckOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { createOvertimeService, Employee, Approver, OvertimeRequestData, OvertimeProject } from '../../../services/createOvertime.service';
import { excelProcessingService, EmployeeMatchResult } from '../../../services/excelProcessing.service';
import { projectService, Project } from '../../../services/project.service';
import { AuthUser } from '../../../services/auth.service';
import AddMoreEmployeesModal from '../modals/AddMoreEmployeesModal';
import { overtimeManagementService, OvertimeRequestSummary } from '../../../services/overtimeManagement.service';
import { duplicateOvertimeCheckService, DuplicateCheckResult } from '../../../services/duplicateOvertimeCheck.service';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface CreateOvertimePageProps {
  user?: AuthUser;
  onNavigate?: (page: string, params?: any) => void;
}

interface FormData {
  projectName: string;
  projectCode: string;
  startTime: string;
  endTime: string;
  selectedEmployeeIds: string[];
  selectedApprover: Approver | null;
  overtimeDate: Date;
  isNightShift: boolean;
}

interface FormErrors {
  projectName?: string;
  projectCode?: string;
  startTime?: string;
  endTime?: string;
  employees?: string;
  approver?: string;
  general?: string;
  overtimeDate?: string;
}

// Night shift detection
const isNextDayEndTime = (startTime: string, endTime: string): boolean => {
  if (!startTime || !endTime) return false;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  return (endHour * 60 + endMinute) <= (startHour * 60 + startMinute);
};

// Duration calculation with cross-midnight support
const calculateDuration = (startTime: string, endTime: string, isNightShift: boolean): number => {
  if (!startTime || !endTime) return 0;
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  let startTotal = startHour * 60 + startMinute;
  let endTotal = endHour * 60 + endMinute;
  if (isNightShift || endTotal <= startTotal) {
    endTotal += 24 * 60;
  }
  return Math.max(0, (endTotal - startTotal) / 60);
};

const CreateOvertimePage: React.FC<CreateOvertimePageProps> = ({ user, onNavigate }) => {
  // Form State
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    projectName: '',
    projectCode: '',
    startTime: '',
    endTime: '',
    selectedEmployeeIds: [],
    selectedApprover: null,
    overtimeDate: new Date(),
    isNightShift: false
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Data State
  const [eligibleEmployees, setEligibleEmployees] = useState<Employee[]>([]);
  const [availableApprovers, setAvailableApprovers] = useState<Approver[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  // Loading State
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');

  // UI State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submissionResult, setSubmissionResult] = useState<{
    success: boolean;
    message: string;
    requestId?: string;
  } | null>(null);
  const [showAddMoreModal, setShowAddMoreModal] = useState(false);
  const [recentRequestSummary, setRecentRequestSummary] = useState<OvertimeRequestSummary | null>(null);
  const [addMoreSuccess, setAddMoreSuccess] = useState<string | null>(null);

  // Excel Upload State
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  const [excelUploadResult, setExcelUploadResult] = useState<EmployeeMatchResult | null>(null);

  // Duplicate Checking State
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<DuplicateCheckResult | null>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Auto-detect night shift
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const shouldBeNightShift = isNextDayEndTime(formData.startTime, formData.endTime);
      if (shouldBeNightShift && !formData.isNightShift) {
        setFormData(prev => ({ ...prev, isNightShift: true }));
      }
    }
  }, [formData.startTime, formData.endTime]);

  // Initialize
  useEffect(() => {
    initializeData();
    setDefaultTimes();
  }, []);

  // Filter employees
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = eligibleEmployees.filter(emp =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.employeeNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.pin?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(eligibleEmployees);
    }
  }, [searchQuery, eligibleEmployees]);

  // Clear duplicate check on form change
  useEffect(() => {
    if (duplicateCheckResult) {
      setDuplicateCheckResult(null);
    }
  }, [formData.projectCode, formData.startTime, formData.endTime, formData.overtimeDate, formData.selectedEmployeeIds.length]);

  // Load projects
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const projects = await projectService.getActiveProjects();
      setAvailableProjects(projects);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!projectSearchQuery.trim()) return availableProjects;
    const search = projectSearchQuery.toLowerCase();
    return availableProjects.filter(project =>
      project.projectCode.toLowerCase().includes(search) ||
      project.projectName.toLowerCase().includes(search)
    );
  }, [projectSearchQuery, availableProjects]);

  const initializeData = async () => {
    try {
      setIsInitialLoading(true);

      setLoadingMessage('Verifying permissions...');
      if (!user?.hasOvertimeAccess) {
        setErrors({ general: 'You do not have permission to create overtime requests.' });
        setIsInitialLoading(false);
        return;
      }

      setLoadingMessage('Loading approvers...');
      const approvers = await createOvertimeService.getAvailableApprovers();

      setLoadingMessage('Loading employees...');
      const employees = await createOvertimeService.getEligibleEmployees();

      setEligibleEmployees(employees);
      setAvailableApprovers(approvers);
      setFilteredEmployees(employees);

      if (approvers.length > 0) {
        setFormData(prev => ({ ...prev, selectedApprover: approvers[0] }));
      }
    } catch (error) {
      console.error('Error initializing:', error);
      setErrors({ general: 'Failed to load data. Please refresh and try again.' });
    } finally {
      setIsInitialLoading(false);
    }
  };

  const setDefaultTimes = () => {
    const now = new Date();
    const startTime = new Date(now);
    startTime.setMinutes(0, 0, 0);
    const endTime = new Date(startTime);
    endTime.setHours(endTime.getHours() + 2);
    setFormData(prev => ({
      ...prev,
      startTime: startTime.toTimeString().slice(0, 5),
      endTime: endTime.toTimeString().slice(0, 5)
    }));
  };

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};
    switch (step) {
      case 0:
        if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
        if (!formData.projectCode.trim()) newErrors.projectCode = 'Project code is required';
        break;
      case 1:
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 7);
        if (formData.overtimeDate > maxDate) newErrors.overtimeDate = 'Cannot select dates more than 7 days ahead';
        if (!formData.startTime) newErrors.startTime = 'Start time is required';
        if (!formData.endTime) newErrors.endTime = 'End time is required';
        if (formData.startTime && formData.endTime) {
          const duration = calculateDuration(formData.startTime, formData.endTime, formData.isNightShift);
          if (duration > 16) newErrors.endTime = 'Duration cannot exceed 16 hours';
          if (duration < 0.5) newErrors.endTime = 'Duration must be at least 30 minutes';
        }
        break;
      case 2:
        if (formData.selectedEmployeeIds.length === 0) newErrors.employees = 'At least one employee must be selected';
        break;
      case 3:
        if (!formData.selectedApprover) newErrors.approver = 'An approver must be selected';
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
      setErrors({});
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setErrors({});
  };

  // Project selection
  const handleProjectSelect = (project: Project) => {
    setFormData(prev => ({
      ...prev,
      projectCode: project.projectCode,
      projectName: project.projectName
    }));
    setProjectSearchQuery('');
    setErrors(prev => ({ ...prev, projectName: undefined, projectCode: undefined }));
  };

  // Excel upload
  const handleExcelUpload = useCallback(async (file: any) => {
    const validation = excelProcessingService.validateExcelFile(file);
    if (!validation.valid) {
      message.error(validation.error || 'Invalid file');
      return false;
    }

    setIsUploadingExcel(true);
    setExcelUploadResult(null);

    try {
      const processingResult = await excelProcessingService.processExcelFile(file);
      if (!processingResult.success) throw new Error(processingResult.errors.join(', '));

      const matchingResult = excelProcessingService.matchEmployees(processingResult.auxNumbers, eligibleEmployees);
      setExcelUploadResult(matchingResult);

      if (matchingResult.matched?.length > 0) {
        const matchedIds = matchingResult.matched.map(emp => emp.id);
        setFormData(prev => ({
          ...prev,
          selectedEmployeeIds: [...new Set([...prev.selectedEmployeeIds, ...matchedIds])]
        }));
        message.success(`Added ${matchingResult.matched.length} employees from Excel`);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to process Excel file');
    } finally {
      setIsUploadingExcel(false);
    }
    return false;
  }, [eligibleEmployees]);

  // Employee handlers
  const handleEmployeeToggle = (employeeId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(employeeId)
        ? prev.selectedEmployeeIds.filter(id => id !== employeeId)
        : [...prev.selectedEmployeeIds, employeeId]
    }));
  };

  const handleSelectAll = () => {
    setFormData(prev => ({ ...prev, selectedEmployeeIds: filteredEmployees.map(emp => emp.id) }));
  };

  const handleClearAll = () => {
    setFormData(prev => ({ ...prev, selectedEmployeeIds: [] }));
    setExcelUploadResult(null);
  };

  // Duplicate check
  const checkForDuplicates = useCallback(async (): Promise<boolean> => {
    try {
      setIsCheckingDuplicates(true);
      setDuplicateCheckResult(null);

      const overtimeDate = formData.overtimeDate;
      const startDateTime = new Date(overtimeDate);
      const endDateTime = new Date(overtimeDate);
      const [startHour, startMinute] = formData.startTime.split(':').map(Number);
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);
      startDateTime.setHours(startHour, startMinute, 0, 0);
      if (formData.isNightShift) endDateTime.setDate(endDateTime.getDate() + 1);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      const result = await duplicateOvertimeCheckService.checkDuplicateOvertime(
        formData.selectedEmployeeIds, overtimeDate, formData.projectCode,
        formData.projectName, startDateTime, endDateTime
      );
      setDuplicateCheckResult(result);

      if (!result.canProceedWithAll) {
        message.error('Conflicts found. Please resolve before submitting.');
        return false;
      }
      if (!result.hasConflicts) {
        message.success('No conflicts found');
      }
      return true;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return true;
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, [formData]);

  // Submit
  const handleSubmit = async () => {
    if (!validateStep(3) || !user) return;

    const userId = user.employeeNo || user.employeeNumber || user.uid;
    const userName = user.name || user.displayName || '';
    if (!userId) {
      setSubmissionResult({ success: false, message: 'User identification error. Please logout and login again.' });
      return;
    }

    const canProceed = await checkForDuplicates();
    if (!canProceed) return;

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const overtimeDate = formData.overtimeDate;
      const startDateTime = new Date(overtimeDate);
      const endDateTime = new Date(overtimeDate);
      const [startHour, startMinute] = formData.startTime.split(':').map(Number);
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);
      startDateTime.setHours(startHour, startMinute, 0, 0);
      if (formData.isNightShift) endDateTime.setDate(endDateTime.getDate() + 1);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      const durationHrs = calculateDuration(formData.startTime, formData.endTime, formData.isNightShift);

      const project: OvertimeProject = {
        id: 'proj_1',
        projectName: formData.projectName.trim(),
        projectCode: formData.projectCode.trim(),
        startTime: startDateTime,
        endTime: endDateTime,
        duration: durationHrs,
        overtimeDate: formData.overtimeDate,
        isNightShift: formData.isNightShift,
        hasLongShift: formData.isNightShift
      };

      const requestData: OvertimeRequestData = {
        projects: [project],
        employeeIds: formData.selectedEmployeeIds,
        requesterId: userId,
        requesterName: userName || 'Unknown User',
        selectedApproverId: formData.selectedApprover!.id,
        selectedApproverName: formData.selectedApprover!.name,
        overtimeDate: formData.overtimeDate,
        isNightShift: formData.isNightShift
      };

      const result = await createOvertimeService.createOvertimeRequest(requestData);
      setSubmissionResult(result);

      if (result.success) {
        message.success('Overtime request created successfully');
        const summary = await overtimeManagementService.getOvertimeRequestSummary(result.requestId!);
        setRecentRequestSummary(summary);

        setFormData({
          projectName: '', projectCode: '', startTime: '', endTime: '',
          selectedEmployeeIds: [], selectedApprover: null,
          overtimeDate: new Date(), isNightShift: false
        });
        setDefaultTimes();
        setSearchQuery('');
        setExcelUploadResult(null);
        setDuplicateCheckResult(null);
        setCurrentStep(0);
        if (availableApprovers.length > 0) {
          setFormData(prev => ({ ...prev, selectedApprover: availableApprovers[0] }));
        }
      }
    } catch (error) {
      console.error('Error submitting:', error);
      setSubmissionResult({
        success: false,
        message: `Submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Computed
  const selectedEmployees = useMemo(() =>
    eligibleEmployees.filter(emp => formData.selectedEmployeeIds.includes(emp.id)),
    [eligibleEmployees, formData.selectedEmployeeIds]
  );

  const durationHours = useMemo(() =>
    calculateDuration(formData.startTime, formData.endTime, formData.isNightShift),
    [formData.startTime, formData.endTime, formData.isNightShift]
  );

  const getNextDayLabel = () => {
    if (!formData.isNightShift) return '';
    const nextDay = new Date(formData.overtimeDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  // Loading
  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Spin size="large" tip={loadingMessage} />
      </div>
    );
  }

  // Access denied
  if (!user?.hasOvertimeAccess && !isInitialLoading) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You do not have permission to create overtime requests."
        extra={<Button type="primary" onClick={() => onNavigate?.('dashboard')}>Back to Dashboard</Button>}
      />
    );
  }

  // General error
  if (errors.general && !duplicateCheckResult) {
    return (
      <Result
        status="error"
        title="Error Loading Form"
        subTitle={errors.general}
        extra={<Button type="primary" onClick={initializeData}>Try Again</Button>}
      />
    );
  }

  // Step content renderers
  const renderProjectStep = () => (
    <div>
      {formData.projectName && formData.projectCode ? (
        <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f', marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>Selected Project</Text>
              <br />
              <Text strong style={{ fontSize: 16 }}>{formData.projectName}</Text>
              <br />
              <Text type="secondary">Code: {formData.projectCode}</Text>
            </Col>
            <Col>
              <Button onClick={() => setFormData(prev => ({ ...prev, projectCode: '', projectName: '' }))}>
                Change Project
              </Button>
            </Col>
          </Row>
        </Card>
      ) : (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Search and Select Project <Text type="danger">*</Text></Text>
          </div>
          <Input
            placeholder="Search by project code or name..."
            prefix={<SearchOutlined />}
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            allowClear
            disabled={isLoadingProjects}
            style={{ marginBottom: 12 }}
          />

          {isLoadingProjects ? (
            <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
          ) : filteredProjects.length > 0 ? (
            <List
              size="small"
              bordered
              style={{ maxHeight: 400, overflow: 'auto' }}
              dataSource={filteredProjects}
              renderItem={(project) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleProjectSelect(project)}
                  actions={[<Button type="link" size="small">Select</Button>]}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<ProjectOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                    title={project.projectName}
                    description={`Code: ${project.projectCode}`}
                  />
                </List.Item>
              )}
            />
          ) : availableProjects.length > 0 ? (
            <Empty description={`No projects matching "${projectSearchQuery}"`}>
              <Button onClick={() => setProjectSearchQuery('')}>Clear Search</Button>
            </Empty>
          ) : (
            <Alert
              message="No Projects Available"
              description="Please add projects in Project Management before creating overtime requests."
              type="warning"
              showIcon
              action={<Button icon={<ReloadOutlined />} onClick={loadProjects}>Refresh</Button>}
            />
          )}

          {errors.projectName && <Alert message={errors.projectName} type="error" showIcon style={{ marginTop: 8 }} banner />}
        </div>
      )}
    </div>
  );

  const renderScheduleStep = () => (
    <div>
      {formData.isNightShift && (
        <Alert
          message="Cross-midnight shift detected"
          description={`End time (${formData.endTime}) will be treated as next day (${getNextDayLabel()}). Duration: ${durationHours.toFixed(1)} hours.`}
          type="info"
          icon={<MoonFilled />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <div style={{ marginBottom: 8 }}><Text strong>Overtime Date <Text type="danger">*</Text></Text></div>
          <Input
            type="date"
            value={formData.overtimeDate.toISOString().split('T')[0]}
            onChange={(e) => setFormData(prev => ({ ...prev, overtimeDate: new Date(e.target.value) }))}
            max={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            disabled={isSubmitting}
          />
          {errors.overtimeDate && <Text type="danger" style={{ fontSize: 12 }}>{errors.overtimeDate}</Text>}
        </Col>
        <Col span={4}>
          <div style={{ marginBottom: 8 }}><Text strong>Duration</Text></div>
          <Statistic
            value={durationHours.toFixed(1)}
            suffix="hours"
            valueStyle={{ fontSize: 18, fontWeight: 700, color: durationHours > 0 ? '#1890ff' : '#ff4d4f' }}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <div style={{ marginBottom: 8 }}><Text strong>Start Time <Text type="danger">*</Text></Text></div>
          <Input
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
            disabled={isSubmitting}
          />
          {errors.startTime && <Text type="danger" style={{ fontSize: 12 }}>{errors.startTime}</Text>}
        </Col>
        <Col span={6}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>End Time <Text type="danger">*</Text></Text>
            {formData.isNightShift && <Tag color="blue" style={{ marginLeft: 4, fontSize: 10 }}>Next Day</Tag>}
          </div>
          <Input
            type="time"
            value={formData.endTime}
            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
            disabled={isSubmitting}
          />
          {errors.endTime && <Text type="danger" style={{ fontSize: 12 }}>{errors.endTime}</Text>}
        </Col>
        <Col span={6}>
          <div style={{ marginBottom: 8 }}><Text strong>Cross-Midnight</Text></div>
          <Switch
            checked={formData.isNightShift}
            onChange={(checked) => setFormData(prev => ({ ...prev, isNightShift: checked }))}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </Col>
      </Row>

      {durationHours > 0 && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <Text>
            Schedule: {formData.startTime} → {formData.endTime}
            {formData.isNightShift && ` (ends ${getNextDayLabel()})`}
            {' | '}Total: <Text strong>{durationHours.toFixed(1)} hours</Text>
          </Text>
        </Card>
      )}
    </div>
  );

  const renderEmployeeStep = () => (
    <div>
      <Alert
        message="All Employees Available for Selection"
        description="Select any eligible employee now. The system will automatically validate their attendance tomorrow morning at 6:00 AM."
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Excel Upload */}
      <Collapse style={{ marginBottom: 16 }}>
        <Panel
          header={<Space><FileExcelOutlined /><span>Bulk Upload via Excel</span></Space>}
          key="excel"
        >
          <Alert
            message="Upload Instructions"
            description="Excel file should have an AUX column with employee numbers. All matched employees will be added."
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            action={
              <Button size="small" onClick={() => excelProcessingService.downloadSampleTemplate()}>
                Download Template
              </Button>
            }
          />
          <Upload accept=".xlsx,.xls" beforeUpload={handleExcelUpload} showUploadList={false} disabled={isUploadingExcel}>
            <Button icon={<UploadOutlined />} loading={isUploadingExcel}>
              {isUploadingExcel ? 'Processing...' : 'Upload Excel File'}
            </Button>
          </Upload>

          {excelUploadResult && (
            <div style={{ marginTop: 12 }}>
              <Alert
                message={`${excelUploadResult.matched.length} of ${excelUploadResult.total} employees matched`}
                type={excelUploadResult.matched.length > 0 ? 'success' : 'warning'}
                showIcon
              />
              {excelUploadResult.matched.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {excelUploadResult.matched.map((emp: any) => (
                    <Tag key={emp.id} color="green" style={{ margin: '2px' }}>
                      {emp.name} ({emp.pin || emp.employeeNumber})
                    </Tag>
                  ))}
                </div>
              )}
              {excelUploadResult.failed.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Not Found:</Text>
                  {excelUploadResult.failed.slice(0, 10).map((aux: string) => (
                    <Tag key={aux} color="red" style={{ margin: '2px' }}>{aux}</Tag>
                  ))}
                  {excelUploadResult.failed.length > 10 && (
                    <Tag>+{excelUploadResult.failed.length - 10} more</Tag>
                  )}
                </div>
              )}
            </div>
          )}
        </Panel>
      </Collapse>

      {/* Selected Summary */}
      {formData.selectedEmployeeIds.length > 0 && (
        <Card
          size="small"
          title={`Selected Employees (${formData.selectedEmployeeIds.length})`}
          extra={<Button type="link" danger size="small" onClick={handleClearAll}>Clear All</Button>}
          style={{ marginBottom: 16 }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {selectedEmployees.slice(0, 15).map(emp => (
              <Tag
                key={emp.id}
                closable
                onClose={() => handleEmployeeToggle(emp.id)}
                color="blue"
              >
                {emp.name}
              </Tag>
            ))}
            {selectedEmployees.length > 15 && <Tag>+{selectedEmployees.length - 15} more</Tag>}
          </div>
        </Card>
      )}

      {errors.employees && <Alert message={errors.employees} type="error" showIcon style={{ marginBottom: 12 }} banner />}

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Statistic title="Total Eligible" value={eligibleEmployees.length} valueStyle={{ color: '#1890ff' }} />
        </Col>
        <Col span={12}>
          <Statistic title="Selected" value={formData.selectedEmployeeIds.length} valueStyle={{ color: '#722ed1' }} />
        </Col>
      </Row>

      {/* Search & Select */}
      <Input
        placeholder="Search by name, AUX number, designation, department..."
        prefix={<SearchOutlined />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
        disabled={isSubmitting}
      />

      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" size="small" onClick={handleSelectAll} disabled={filteredEmployees.length === 0}>
          Select All ({filteredEmployees.length})
        </Button>
        <Text type="secondary">Showing {filteredEmployees.length} employees</Text>
      </Space>

      <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 8 }}>
        {filteredEmployees.length > 0 ? (
          <List
            size="small"
            dataSource={filteredEmployees}
            renderItem={(employee) => {
              const isSelected = formData.selectedEmployeeIds.includes(employee.id);
              return (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? '#e6f7ff' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent'
                  }}
                  onClick={() => handleEmployeeToggle(employee.id)}
                >
                  <List.Item.Meta
                    avatar={
                      <Checkbox checked={isSelected} onChange={() => handleEmployeeToggle(employee.id)} />
                    }
                    title={<Text strong={isSelected}>{employee.name}</Text>}
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        PIN: {employee.pin || employee.employeeNumber || 'N/A'} | {employee.designation}
                        {employee.department && employee.department !== 'No department' ? ` | ${employee.department}` : ''}
                      </Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty description={searchQuery ? 'No employees found matching your search.' : 'No employees available.'} />
        )}
      </div>
    </div>
  );

  const renderApproverStep = () => (
    <div>
      {/* Approver Selection */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}><Text strong>Select Approver <Text type="danger">*</Text></Text></div>
        <Select
          style={{ width: '100%' }}
          placeholder="Select Approver"
          value={formData.selectedApprover?.id || undefined}
          onChange={(value) => {
            const approver = availableApprovers.find(a => a.id === value);
            setFormData(prev => ({ ...prev, selectedApprover: approver || null }));
          }}
          showSearch
          optionFilterProp="children"
          size="large"
        >
          {availableApprovers.map(approver => (
            <Option key={approver.id} value={approver.id}>
              {approver.name} - {approver.designation} ({approver.department})
            </Option>
          ))}
        </Select>
        {errors.approver && <Text type="danger" style={{ fontSize: 12 }}>{errors.approver}</Text>}
      </div>

      {/* Request Summary */}
      <Card title="Request Summary" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Project">{formData.projectName}</Descriptions.Item>
          <Descriptions.Item label="Code">{formData.projectCode}</Descriptions.Item>
          <Descriptions.Item label="Date">{formData.overtimeDate.toLocaleDateString()}</Descriptions.Item>
          <Descriptions.Item label="Time">
            {formData.startTime} - {formData.endTime}
            {formData.isNightShift && ` (ends ${getNextDayLabel()})`}
            {` (${durationHours.toFixed(1)}h)`}
          </Descriptions.Item>
          <Descriptions.Item label="Employees">{formData.selectedEmployeeIds.length} selected</Descriptions.Item>
          <Descriptions.Item label="Approver">{formData.selectedApprover?.name || 'Not selected'}</Descriptions.Item>
          {formData.isNightShift && (
            <Descriptions.Item label="Shift Type">
              <Tag icon={<MoonFilled />} color="blue">Night Shift (Cross-Midnight)</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Attendance Validation Note */}
      <Alert
        message="Attendance Validation"
        description="Attendance for selected employees will be validated automatically tomorrow morning at 6:00 AM. You can review validation results on your dashboard."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Conflict Check */}
      <Card size="small" title="Pre-Submission Check" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Check if any selected employees already have overtime assignments that might conflict with this request.
        </Paragraph>
        <Button
          icon={<SearchOutlined />}
          onClick={checkForDuplicates}
          loading={isCheckingDuplicates}
          disabled={formData.selectedEmployeeIds.length === 0}
          type="primary"
          ghost
        >
          Check for Conflicts
        </Button>
      </Card>

      {/* Duplicate Check Results */}
      {duplicateCheckResult && duplicateCheckResult.hasConflicts && (
        <Alert
          message={duplicateCheckResult.message}
          description={
            <div>
              <Text>{duplicateCheckResult.conflicts.length} employee(s) with conflicts:</Text>
              <List
                size="small"
                dataSource={duplicateCheckResult.conflicts}
                style={{ marginTop: 8 }}
                renderItem={(conflict, index) => (
                  <List.Item key={index}>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          size="small"
                          style={{ backgroundColor: conflict.canProceed ? '#52c41a' : '#ff4d4f' }}
                        >
                          {conflict.canProceed ? <CheckOutlined /> : <CloseOutlined />}
                        </Avatar>
                      }
                      title={`${conflict.employeeName} (${conflict.employeePin})`}
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Existing: {conflict.existingProjectCode} | {conflict.existingStartTime.toTimeString().slice(0, 5)} - {conflict.existingEndTime.toTimeString().slice(0, 5)}
                          </Text>
                          <br />
                          <Text style={{ fontSize: 12 }} type={conflict.canProceed ? 'success' : 'danger'}>
                            {conflict.reason}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
              {duplicateCheckResult.suggestions.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text strong>Suggestions:</Text>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {duplicateCheckResult.suggestions.map((s, i) => (
                      <li key={i}><Text type="secondary" style={{ fontSize: 13 }}>{s}</Text></li>
                    ))}
                  </ul>
                </div>
              )}
              {!duplicateCheckResult.canProceedWithAll && (
                <Alert message="Cannot submit until conflicts are resolved" type="error" style={{ marginTop: 12 }} showIcon />
              )}
            </div>
          }
          type={duplicateCheckResult.canProceedWithAll ? 'warning' : 'error'}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
    </div>
  );

  const stepContents = [renderProjectStep, renderScheduleStep, renderEmployeeStep, renderApproverStep];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: '0 0 8px' }}>Create Overtime Request</Title>
          <Text type="secondary">Submit overtime requests - attendance will be validated automatically tomorrow morning</Text>
        </div>

        {/* Submission Result */}
        {submissionResult && (
          <Alert
            message={submissionResult.success ? 'Request Created Successfully' : 'Submission Failed'}
            description={
              <div>
                <Text>{submissionResult.message}</Text>
                {submissionResult.success && submissionResult.requestId && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" code>Request ID: {submissionResult.requestId.slice(0, 8)}...</Text>
                  </div>
                )}
                {submissionResult.success && recentRequestSummary && (
                  <div style={{ marginTop: 12 }}>
                    <Button icon={<PlusOutlined />} onClick={() => setShowAddMoreModal(true)}>
                      Add More Employees
                    </Button>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      {recentRequestSummary.employees.length} employee(s) currently selected
                    </Text>
                  </div>
                )}
              </div>
            }
            type={submissionResult.success ? 'success' : 'error'}
            showIcon
            closable
            onClose={() => setSubmissionResult(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {addMoreSuccess && (
          <Alert message={addMoreSuccess} type="success" showIcon closable
            onClose={() => setAddMoreSuccess(null)} style={{ marginBottom: 16 }} />
        )}

        {/* Steps */}
        <Steps
          current={currentStep}
          style={{ marginBottom: 24 }}
          onChange={(step) => {
            if (step < currentStep) {
              setCurrentStep(step);
              setErrors({});
            } else if (step === currentStep + 1 && validateStep(currentStep)) {
              setCurrentStep(step);
              setErrors({});
            }
          }}
          items={[
            { title: 'Project', icon: <ProjectOutlined /> },
            { title: 'Schedule', icon: <CalendarOutlined /> },
            { title: 'Employees', icon: <TeamOutlined /> },
            { title: 'Approver', icon: <UserOutlined /> }
          ]}
        />

        {/* Step Content */}
        <Card bordered={false} style={{ marginBottom: 24 }}>
          {stepContents[currentStep]()}
        </Card>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {currentStep > 0 ? (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handlePrevious}
              disabled={isSubmitting || isCheckingDuplicates}
              size="large"
            >
              Previous
            </Button>
          ) : <div />}

          {currentStep < 3 ? (
            <Button
              type="primary"
              onClick={handleNext}
              disabled={isSubmitting || isCheckingDuplicates}
              size="large"
            >
              Next <ArrowRightOutlined />
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleSubmit}
              loading={isSubmitting || isCheckingDuplicates}
              disabled={
                !formData.projectName || !formData.projectCode ||
                !formData.startTime || !formData.endTime ||
                formData.selectedEmployeeIds.length === 0 ||
                !formData.selectedApprover ||
                (duplicateCheckResult !== null && !duplicateCheckResult.canProceedWithAll)
              }
              size="large"
              style={{ minWidth: 200 }}
            >
              {isCheckingDuplicates ? 'Checking...' : `Submit Request (${formData.selectedEmployeeIds.length} employees)`}
            </Button>
          )}
        </div>
      </div>

      {/* Add More Modal */}
      {showAddMoreModal && recentRequestSummary && (
        <AddMoreEmployeesModal
          requestSummary={recentRequestSummary}
          user={user!}
          onClose={() => setShowAddMoreModal(false)}
          onSuccess={(msg) => {
            setAddMoreSuccess(msg);
            setRecentRequestSummary(null);
            setTimeout(() => setAddMoreSuccess(null), 5000);
          }}
        />
      )}
    </div>
  );
};

export default CreateOvertimePage;